import React, { useState, useRef } from 'react';
import { FaUpload, FaTrash, FaTimes, FaImage, FaSearchPlus, FaCloudUploadAlt, FaLock } from 'react-icons/fa';

const DEMO_PHOTO_LIMIT = 2;

const DemoPhotoGallery = () => {
  const [photos, setPhotos] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [filePreviews, setFilePreviews] = useState([]);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const dropZoneRef = useRef(null);

  const isLimitReached = photos.length >= DEMO_PHOTO_LIMIT;

  const validateFile = (file) => {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) return false;
    if (file.size > 10 * 1024 * 1024) return false;
    return true;
  };

  const processFiles = (files) => {
    const fileArray = Array.from(files).filter(validateFile);
    if (fileArray.length === 0) return;

    const previews = fileArray.map(file => ({
      file,
      url: URL.createObjectURL(file),
      name: file.name
    }));

    setSelectedFiles(prev => [...prev, ...fileArray]);
    setFilePreviews(prev => [...prev, ...previews]);
  };

  const handleFileSelect = (e) => {
    if (e.target.files?.length > 0) processFiles(e.target.files);
    e.target.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files?.length > 0) processFiles(e.dataTransfer.files);
  };

  const removeSelectedFile = (index) => {
    URL.revokeObjectURL(filePreviews[index].url);
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setFilePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpload = () => {
    if (selectedFiles.length === 0) return;

    const remaining = DEMO_PHOTO_LIMIT - photos.length;
    const toUpload = selectedFiles.slice(0, remaining);

    const newPhotos = toUpload.map((file, i) => ({
      id: `demo-${Date.now()}-${i}`,
      url: filePreviews[i].url,
      filename: file.name,
      comment: '',
    }));

    setPhotos(prev => [...prev, ...newPhotos]);
    setSelectedFiles([]);
    setFilePreviews([]);
  };

  const handleDeletePhoto = (id) => {
    setPhotos(prev => {
      const photo = prev.find(p => p.id === id);
      if (photo) URL.revokeObjectURL(photo.url);
      return prev.filter(p => p.id !== id);
    });
  };

  return (
    <>
      <div className="bg-white rounded-xl shadow-medium p-6 space-y-6">
        <h3 className="text-2xl font-bold text-neutral-800 flex items-center gap-3 pb-3 border-b border-neutral-300">
          <div className="w-10 h-10 bg-gradient-secondary rounded-full flex items-center justify-center">
            <FaImage className="text-white text-xl" />
          </div>
          Trip Photos
        </h3>

        {/* Demo Banner */}
        <div className={`flex items-center justify-between p-3 rounded-lg border ${isLimitReached ? 'bg-amber-50 border-amber-200' : 'bg-sky-50 border-sky-200'}`}>
          <p className={`text-sm font-medium ${isLimitReached ? 'text-amber-700' : 'text-sky-700'}`}>
            {isLimitReached
              ? 'Demo limit reached. Subscribe to Solo plan for unlimited photos.'
              : `Demo mode: ${photos.length}/${DEMO_PHOTO_LIMIT} photos (local only)`}
          </p>
        </div>

        {/* Upload Zone */}
        {!isLimitReached && (
          <div
            ref={dropZoneRef}
            onDragEnter={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
            className={`relative rounded-lg p-8 border-2 border-dashed transition-all duration-200 ${
              isDragging ? 'border-primary-500 bg-primary-50 scale-[1.02]' : 'border-neutral-300 bg-gradient-to-br from-primary-50 to-secondary-50 hover:border-primary-400'
            }`}
          >
            <div className="flex flex-col items-center gap-4">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center ${isDragging ? 'bg-primary-500 scale-110' : 'bg-gradient-primary'}`}>
                <FaCloudUploadAlt className="text-3xl text-white" />
              </div>
              <div className="text-center">
                <p className="text-lg font-semibold text-neutral-700">
                  {isDragging ? 'Drop photos here!' : 'Drag & drop photos here'}
                </p>
                <p className="text-neutral-500 mt-1">or</p>
              </div>
              <label className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-primary text-white rounded-lg font-semibold shadow-travel hover:shadow-elevated hover:scale-105 transition-all duration-200 cursor-pointer">
                <FaUpload />
                Browse Files
                <input
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                  onChange={handleFileSelect}
                  multiple
                  className="hidden"
                />
              </label>
              <p className="text-sm text-neutral-500">Supports: JPG, PNG, GIF, WebP (max 10MB each)</p>
            </div>
          </div>
        )}

        {/* Selected Files Preview */}
        {filePreviews.length > 0 && (
          <div className="bg-white rounded-lg border border-neutral-200 p-4 space-y-4 animate-slide-down">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold text-neutral-700">{filePreviews.length} photo{filePreviews.length !== 1 ? 's' : ''} selected</h4>
              <button onClick={() => { filePreviews.forEach(p => URL.revokeObjectURL(p.url)); setSelectedFiles([]); setFilePreviews([]); }} className="text-sm text-red-600 hover:text-red-700 font-medium">Clear All</button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {filePreviews.map((preview, index) => (
                <div key={index} className="relative group">
                  <div className="aspect-square rounded-lg overflow-hidden bg-neutral-100 border border-neutral-200">
                    <img src={preview.url} alt={preview.name} className="w-full h-full object-cover" />
                  </div>
                  <button onClick={() => removeSelectedFile(index)} className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-md hover:bg-red-600">
                    <FaTimes className="text-xs" />
                  </button>
                </div>
              ))}
            </div>
            <button
              onClick={handleUpload}
              className="w-full px-6 py-3 bg-gradient-secondary text-white rounded-lg font-semibold shadow-secondary hover:shadow-elevated hover:scale-[1.02] transition-all duration-200"
            >
              Upload {Math.min(filePreviews.length, DEMO_PHOTO_LIMIT - photos.length)} Photo{Math.min(filePreviews.length, DEMO_PHOTO_LIMIT - photos.length) !== 1 ? 's' : ''}
            </button>
          </div>
        )}

        {/* Photos Grid */}
        {photos.length === 0 ? (
          <div className="text-center py-12 bg-neutral-50 rounded-lg border-2 border-dashed border-neutral-300">
            <div className="w-20 h-20 bg-neutral-200 rounded-full flex items-center justify-center mx-auto mb-4">
              <FaImage className="text-4xl text-neutral-400" />
            </div>
            <p className="text-neutral-600 font-medium text-lg">No photos yet. Upload some memories!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {photos.map((photo) => (
              <div key={photo.id} className="bg-white rounded-xl shadow-medium overflow-hidden hover:shadow-elevated transition-all duration-200 group">
                <div className="relative aspect-[4/3] bg-neutral-100">
                  <img src={photo.url} alt={photo.filename} className="w-full h-full object-cover cursor-pointer" onClick={() => { setLightboxUrl(photo.url); setLightboxOpen(true); }} />
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center cursor-pointer" onClick={() => { setLightboxUrl(photo.url); setLightboxOpen(true); }}>
                    <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center">
                      <FaSearchPlus className="text-2xl text-primary-500" />
                    </div>
                  </div>
                </div>
                <div className="p-4">
                  <p className="text-neutral-500 text-sm italic">Demo photo - local only</p>
                  <div className="flex gap-2 pt-2 border-t border-neutral-100 mt-2">
                    <button onClick={() => handleDeletePhoto(photo.id)} className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-red-50 text-red-600 rounded-lg font-medium hover:bg-red-100 transition-colors text-sm">
                      <FaTrash /> Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Simple Lightbox */}
      {lightboxOpen && (
        <div className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center" onClick={() => setLightboxOpen(false)}>
          <button className="absolute top-4 right-4 text-white text-3xl hover:text-neutral-300 z-10" onClick={() => setLightboxOpen(false)}>
            <FaTimes />
          </button>
          <img src={lightboxUrl} alt="Full size" className="max-w-[90vw] max-h-[90vh] object-contain" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </>
  );
};

export default DemoPhotoGallery;
